/**
 * esptool-js orchestration.
 *
 * Owns the Transport/ESPLoader pair and the serial port lifetime, and keeps all
 * library-specific details behind a small event-driven surface so the React
 * layer never touches esptool-js directly.
 *
 * Two behaviours here are deliberate and worth not "simplifying" later:
 *
 *  - Flash mode, frequency, and size default to "keep". esptool patches those
 *    fields into the bootloader header at write time; overriding them with
 *    guesses is how a board that flashes cleanly still fails to boot. "keep"
 *    writes exactly what the Arduino IDE compiled.
 *
 *  - calculateMD5Hash is always supplied. It is optional in the API, and when
 *    omitted esptool-js skips post-write verification silently -- a corrupted
 *    write then reports success.
 */

import { ESPLoader, Transport } from "esptool-js";
import type { FlashFreqValues, FlashModeValues, FlashSizeValues, IEspLoaderTerminal } from "esptool-js";
import { md5Hex } from "./md5";
import { chipFromDetectedName, type ChipProfile } from "./chips";
import { parseFlashSizeToBytes } from "./binaryInfo";
import type { FlashPhase } from "./errors";

export interface DeviceInfo {
  /** Full description from esptool, e.g. "ESP32-S3 (QFN56) (revision v0.2)". */
  description: string;
  chip: ChipProfile | undefined;
  chipName: string;
  /** Reported flash capacity, e.g. "4MB". Null when detection failed. */
  flashSize: string | null;
  flashSizeBytes: number | null;
  /** USB vendor/product identifiers of the bridge. */
  portInfo: string;
  usingNativeUsb: boolean;
}

export interface FlashRegion {
  fileName: string;
  data: Uint8Array;
  address: number;
}

export interface FlashSettings {
  baudRate: number;
  eraseAll: boolean;
  compress: boolean;
  flashMode: FlashModeValues;
  flashFreq: FlashFreqValues;
  flashSize: FlashSizeValues;
}

export interface FlashProgress {
  phase: FlashPhase;
  /** 0..1 across every region. */
  overall: number;
  /** Index into the regions array. */
  fileIndex: number;
  fileName: string;
  fileWritten: number;
  fileTotal: number;
  bytesWritten: number;
  bytesTotal: number;
  /** Bytes per second, smoothed. Null until enough samples exist. */
  speed: number | null;
  /** Seconds remaining. Null until a speed estimate exists. */
  etaSeconds: number | null;
  elapsedSeconds: number;
}

export type LogLevel = "info" | "warn" | "error" | "success" | "device";

export interface FlashServiceEvents {
  onLog: (level: LogLevel, message: string) => void;
  onProgress: (progress: FlashProgress) => void;
  onPhase: (phase: FlashPhase) => void;
  /** Fired when the USB device vanishes unexpectedly. */
  onDeviceLost: () => void;
  /** Raw bytes from the serial monitor. */
  onMonitorData: (text: string) => void;
}

export const DEFAULT_SETTINGS: FlashSettings = {
  baudRate: 921600,
  eraseAll: false,
  compress: true,
  flashMode: "keep",
  flashFreq: "keep",
  flashSize: "keep",
};

export const BAUD_RATES = [115200, 230400, 460800, 921600, 1500000] as const;

/** PIDs that indicate the chip's built-in USB rather than an external UART bridge. */
const NATIVE_USB_PIDS = new Set([0x1001]);

export class FlashService {
  private transport: Transport | null = null;
  private loader: ESPLoader | null = null;
  private port: SerialPort | null = null;
  private monitorRunning = false;
  private device: DeviceInfo | null = null;

  constructor(private readonly events: FlashServiceEvents) {}

  get isConnected(): boolean {
    return this.loader !== null;
  }

  get deviceInfo(): DeviceInfo | null {
    return this.device;
  }

  get isMonitoring(): boolean {
    return this.monitorRunning;
  }

  /** True once the user has granted access to a port in this session. */
  get hasPort(): boolean {
    return this.port !== null;
  }

  /**
   * Show the browser's port picker. Kept separate from connect() so a cancelled
   * chooser can be reported as a notice rather than a failure.
   */
  async selectPort(): Promise<void> {
    if (!("serial" in navigator)) {
      throw new Error("Web Serial is not available in this browser.");
    }
    this.port = await navigator.serial.requestPort();
  }

  /** Open the port, sync with the ROM loader, and identify the chip. */
  async connect(baudRate: number): Promise<DeviceInfo> {
    if (!this.port) {
      throw new Error("No serial port has been selected.");
    }

    const terminal: IEspLoaderTerminal = {
      clean: () => {},
      // esptool-js emits progress as bare writes without newlines; routing both
      // through the same handler keeps ordering intact in the log panel.
      write: (data) => this.emitLibraryLog(data),
      writeLine: (data) => this.emitLibraryLog(data),
    };

    this.transport = new Transport(this.port, false);
    this.transport.setDeviceLostCallback(() => {
      this.events.onDeviceLost();
    });

    this.loader = new ESPLoader({
      transport: this.transport,
      baudrate: baudRate,
      terminal,
      debugLogging: false,
    });

    const description = await this.loader.main();
    const chipName = this.loader.chip?.CHIP_NAME ?? description;

    let flashSize: string | null = null;
    try {
      flashSize = await this.loader.detectFlashSize();
    } catch {
      // Non-fatal: a few bridges refuse the SPI ID read. We simply skip the
      // capacity check rather than aborting an otherwise fine connection.
      this.events.onLog("warn", "Could not read the flash chip size; capacity checks will be skipped.");
    }

    const pid = this.transport.getPid();

    this.device = {
      description,
      chip: chipFromDetectedName(description) ?? chipFromDetectedName(chipName),
      chipName,
      flashSize,
      flashSizeBytes: flashSize ? parseFlashSizeToBytes(flashSize) : null,
      portInfo: this.transport.getInfo(),
      usingNativeUsb: pid !== undefined && NATIVE_USB_PIDS.has(pid),
    };

    return this.device;
  }

  /**
   * Write every region, then reset the board so the new firmware runs.
   * Regions must already be validated -- this method does no layout checking.
   */
  async flash(regions: FlashRegion[], settings: FlashSettings): Promise<void> {
    const loader = this.loader;
    if (!loader) {
      throw new Error("Not connected to a board.");
    }
    if (regions.length === 0) {
      throw new Error("No files selected to flash.");
    }

    const sizes = regions.map((region) => region.data.length);
    const bytesTotal = sizes.reduce((sum, size) => sum + size, 0);
    // Byte count of everything before region i, for overall progress.
    const priorBytes = sizes.map((_, index) => sizes.slice(0, index).reduce((a, b) => a + b, 0));

    const startedAt = performance.now();
    let lastPhase: FlashPhase = settings.eraseAll ? "erase" : "write";
    this.events.onPhase(lastPhase);

    if (settings.eraseAll) {
      this.events.onLog(
        "info",
        "Erasing the entire flash. On a 4 MB board this usually takes 10-30 seconds with no visible progress -- that is expected.",
      );
    }

    const emitProgress = (fileIndex: number, written: number, total: number) => {
      // The first progress callback means erasing is done and bytes are moving.
      if (lastPhase !== "write") {
        lastPhase = "write";
        this.events.onPhase("write");
      }

      const bytesWritten = priorBytes[fileIndex] + written;
      const elapsedSeconds = (performance.now() - startedAt) / 1000;
      // Ignore the first half-second; early samples produce wild rate estimates.
      const speed = elapsedSeconds > 0.5 ? bytesWritten / elapsedSeconds : null;

      this.events.onProgress({
        phase: "write",
        overall: bytesTotal > 0 ? bytesWritten / bytesTotal : 0,
        fileIndex,
        fileName: regions[fileIndex]?.fileName ?? "",
        fileWritten: written,
        fileTotal: total,
        bytesWritten,
        bytesTotal,
        speed,
        etaSeconds: speed && speed > 0 ? (bytesTotal - bytesWritten) / speed : null,
        elapsedSeconds,
      });
    };

    await loader.writeFlash({
      fileArray: regions.map((region) => ({ data: region.data, address: region.address })),
      flashMode: settings.flashMode,
      flashFreq: settings.flashFreq,
      flashSize: settings.flashSize,
      eraseAll: settings.eraseAll,
      compress: settings.compress,
      reportProgress: emitProgress,
      calculateMD5Hash: md5Hex,
    });

    this.events.onPhase("reset");
    try {
      await loader.after("hard_reset");
      this.events.onLog("success", "Board reset. The new firmware is running.");
    } catch (error) {
      // A failed reset does not invalidate a good write, so this is a warning.
      this.events.onLog(
        "warn",
        `The firmware was written and verified, but the automatic reset failed (${
          error instanceof Error ? error.message : String(error)
        }). Press the EN/RST button on the board to start it.`,
      );
    }
  }

  /**
   * Reopen the port as a plain serial console so the user can watch the board
   * boot. Requires the loader session to be torn down first: the ESPLoader read
   * loop and rawRead cannot share the same stream.
   */
  async startMonitor(baudRate: number): Promise<void> {
    if (!this.port) throw new Error("No serial port has been selected.");
    if (this.monitorRunning) return;

    await this.closeTransport();

    this.transport = new Transport(this.port, false);
    this.transport.setDeviceLostCallback(() => {
      this.monitorRunning = false;
      this.events.onDeviceLost();
    });

    await this.transport.connect(baudRate);
    this.monitorRunning = true;

    const decoder = new TextDecoder("utf-8", { fatal: false });
    void this.transport
      .rawRead(
        (chunk) => this.events.onMonitorData(decoder.decode(chunk, { stream: true })),
        () => !this.monitorRunning,
      )
      .catch((error: unknown) => {
        if (this.monitorRunning) {
          this.events.onLog("warn", `Serial monitor stopped: ${error instanceof Error ? error.message : String(error)}`);
        }
        this.monitorRunning = false;
      });
  }

  async stopMonitor(): Promise<void> {
    if (!this.monitorRunning) return;
    this.monitorRunning = false;
    await this.closeTransport();
  }

  /** Pulse RTS to reset the board without reflashing. */
  async resetBoard(): Promise<void> {
    if (!this.transport) throw new Error("Not connected to a board.");
    await this.transport.setRTS(true);
    await new Promise((resolve) => setTimeout(resolve, 100));
    await this.transport.setRTS(false);
  }

  async disconnect(): Promise<void> {
    this.monitorRunning = false;
    await this.closeTransport();
    this.loader = null;
    this.device = null;
    this.port = null;
  }

  private async closeTransport(): Promise<void> {
    if (!this.transport) return;
    try {
      this.transport.setDeviceLostCallback(null);
      await this.transport.disconnect();
    } catch {
      // The port may already be gone if the device was unplugged. Nothing to do.
    }
    this.transport = null;
    this.loader = null;
  }

  /**
   * esptool-js writes partial lines and carriage-return progress bars. Collapse
   * those into whole lines so the log panel does not fill with fragments.
   */
  private logBuffer = "";

  private emitLibraryLog(data: string): void {
    this.logBuffer += data;
    const lines = this.logBuffer.split(/\r?\n/);
    this.logBuffer = lines.pop() ?? "";

    for (const line of lines) {
      const text = line.trim();
      if (text.length > 0) this.events.onLog("device", text);
    }
  }
}
